import { type DownloadPriority } from "@/shared/reader";
import { LogPanelState } from "@/shared/state/visualization";
import { type MessagePayload, WorkerManager } from "@/shared/webworker";

import { IndexState } from "./IndexState";
import { Log } from "./log";
import { type IndexRange, LogCache, ScrollDirection } from "./LogCache";
import { type LogFilter, isTextSearchFilter } from "./LogFilter";
import {
  isColumnsChangedEvent,
  isErrorEvent,
  isInitializedEvent,
  isLoadingStateChangeEvent,
  isLogTimeIndexStateChangeEvent,
  isSearchIndexStateChangeEvent,
  LogCommand,
  LogCommandPayloadMap,
  LogCommandResponse,
  LogCommandResponsePayloadMap,
  LogEvent,
  LogEventPayloadMap,
  SOURCE,
} from "./messaging";

export type OnInitializedCallback = (
  error: LogEventPayloadMap[LogEvent.Initialized],
) => void;

export type OnErrorCallback = (
  error: LogEventPayloadMap[LogEvent.Error],
) => void;

export type OnLogsBufferedCallback = (
  data: LogEventPayloadMap[LogEvent.LogsBuffered],
) => void;

export type OnLoadingStateChangedCallback = (
  data: LogEventPayloadMap[LogEvent.LoadingStateChange],
) => void;

export type OnNumLogsChangedCallback = (
  data: LogEventPayloadMap[LogEvent.NumLogsChanged],
) => void;

export type OnColumnsChangedCallback = (
  data: LogEventPayloadMap[LogEvent.ColumnsChanged],
) => void;

export type OnSearchIndexStateChangedCallback = (
  data: LogEventPayloadMap[LogEvent.SearchIndexStateChange],
) => void;

const noop = () => {};

type Realm = "UIThread" | "WebWorker";
type LoadingStates = {
  [K in Realm]: DownloadPriority | false;
};

export class LogManager {
  private abortController: AbortController;
  private loadingStates: LoadingStates = {
    UIThread: false,
    WebWorker: false,
  };
  private logCache: LogCache = new LogCache();

  private onColumnsChanged: OnColumnsChangedCallback = noop;

  // Callback for when the worker is initialized
  private onInitialized: OnInitializedCallback = noop;

  // Callback for when an error has occurred
  private onError: OnErrorCallback = noop;

  // Callback for when we're loading logs
  private onLoadingStateChanged: OnLoadingStateChangedCallback = noop;

  private onNumLogsChanged: OnNumLogsChangedCallback = noop;

  private onSearchIndexStateChanged: OnSearchIndexStateChangedCallback = noop;

  private searchIndexState = IndexState.Empty;

  private workerManager: WorkerManager;

  constructor() {
    this.abortController = new AbortController();

    const worker = new Worker(new URL("./LogService.worker", import.meta.url), {
      type: "module",
      name: `LogService_${crypto.randomUUID()}`,
    });

    this.workerManager = new WorkerManager({
      worker,
      onError: this.onError,
      onMessage: this.onMessage,
      signal: this.abortController.signal,
    });

    this.workerManager.sendCommand({
      data: undefined,
      source: SOURCE,
      type: LogCommand.Init,
    });
  }

  public get logCount() {
    return this.logCache.logCount;
  }

  public get isSearchIndexEmpty() {
    return this.searchIndexState === IndexState.Empty;
  }

  public buildSearchIndex(signal?: AbortSignal) {
    this.workerManager.sendCommand({
      data: undefined,
      source: SOURCE,
      type: LogCommand.BuildTextSearchIndex,
      signal,
    });
  }

  public async clearFilter(signal?: AbortSignal) {
    const response = await this.workerManager.sendCommandAwaitResponse<
      LogCommand,
      LogCommandPayloadMap[LogCommand.ClearFilter],
      LogCommandResponsePayloadMap[LogCommandResponse.TimestampsFiltered]
    >({
      type: LogCommand.ClearFilter,
      data: undefined,
      signal,
      source: SOURCE,
    });

    const timestamps = new BigUint64Array(response.timestamps);
    const sequences = new Uint32Array(response.sequences);
    this.logCache.setTimestampIndex(timestamps, sequences);
    this.onNumLogsChanged({ numLogs: timestamps.length });
  }

  public dispose(): void {
    this.abortController.abort();
    this.workerManager.dispose();
    this.logCache.dispose();
  }

  public async disposeSearchIndex(signal?: AbortSignal) {
    await this.clearFilter(signal);
    this.workerManager.sendCommand({
      data: undefined,
      signal,
      source: SOURCE,
      type: LogCommand.DisposeTextSearchIndex,
    });
  }

  /**
   * Return the highest priority download state currently active, or `false`.
   */
  public downloadState(): DownloadPriority | false {
    const states = Object.values(this.loadingStates);
    for (const state of ["foreground", "background"] as DownloadPriority[]) {
      if (states.includes(state)) {
        return state;
      }
    }

    return false;
  }

  public async filterLogs(
    filter: LogFilter,
    signal?: AbortSignal,
  ): Promise<void> {
    const isFilterEmpty =
      isTextSearchFilter(filter) &&
      filter.query.trim().length === 0 &&
      Object.values(filter.columnFilters).length === 0;
    if (isFilterEmpty) {
      return await this.clearFilter(signal);
    }

    const response = await this.workerManager.sendCommandAwaitResponse<
      LogCommand,
      LogCommandPayloadMap[LogCommand.ApplyFilter],
      LogCommandResponsePayloadMap[LogCommandResponse.TimestampsFiltered]
    >({
      type: LogCommand.ApplyFilter,
      data: filter,
      signal,
      source: SOURCE,
    });

    const timestamps = new BigUint64Array(response.timestamps);
    const sequences = new Uint32Array(response.sequences);
    this.logCache.setTimestampIndex(timestamps, sequences);
    this.onNumLogsChanged({ numLogs: timestamps.length });
  }

  public findLogIndexForTime(time: bigint): number {
    return this.logCache.timestampToIndex(time);
  }

  public getLogByIndex(index: number): Log | undefined {
    return this.logCache.indexToLog(index);
  }

  public getTimestampByIndex(index: number): bigint | undefined {
    return this.logCache.indexToTimestamp(index);
  }

  /**
   * Load logs if necessary, accounting for current display window.
   * Window is an inclusive range in index-space, between [0, logcount],
   * where logcount is the number of logs given to useVirtualizer in LogPanel.
   * logcount is at first equal to topic message count, but can be reduced by filters.
   */
  public async loadLogs(
    currentWindow: IndexRange,
    scrollDirection = ScrollDirection.Forward,
    playbackRate = 1,
    signal?: AbortSignal,
  ): Promise<boolean> {
    const result = this.logCache.getIndicesForMissingData(
      currentWindow,
      scrollDirection,
      playbackRate,
    );

    if (result.isInputRangeInMissing) {
      // If the current window is within missing, set loading indicator
      this.loadingStateChange("UIThread", "foreground");
    } else if (result.missing.length === 0) {
      // Nothing to load
      return false;
    }

    try {
      const logBatch = await Promise.all(
        result.missing.map((range) => {
          return this.workerManager.sendCommandAwaitResponse<
            LogCommand,
            LogCommandPayloadMap[LogCommand.GetLogs],
            LogCommandResponsePayloadMap[LogCommandResponse.LogsLoaded]
          >({
            type: LogCommand.GetLogs,
            data: range,
            signal,
            source: SOURCE,
          });
        }),
      );
      logBatch.forEach((logs) => this.logCache.addLogsToCache(logs));
      this.logCache.trimCache(currentWindow);
      return true;
    } finally {
      if (result.isInputRangeInMissing) {
        this.loadingStateChange("UIThread", false);
      }
    }
  }

  public setEventListener<K extends keyof LogEventPayloadMap>(
    type: K,
    listener: (arg0: LogEventPayloadMap[K]) => void,
  ): void {
    if (type === LogEvent.ColumnsChanged) {
      this.onColumnsChanged = listener as OnColumnsChangedCallback;
      return;
    }

    if (type === LogEvent.Initialized) {
      this.onInitialized = listener as OnInitializedCallback;
      return;
    }

    if (type === LogEvent.Error) {
      this.onError = listener as OnErrorCallback;
      return;
    }

    if (type === LogEvent.LoadingStateChange) {
      this.onLoadingStateChanged = listener as OnLoadingStateChangedCallback;
      return;
    }

    if (type === LogEvent.NumLogsChanged) {
      this.onNumLogsChanged = listener as OnNumLogsChangedCallback;
      return;
    }

    if (type === LogEvent.SearchIndexStateChange) {
      this.onSearchIndexStateChanged =
        listener as OnSearchIndexStateChangedCallback;
      return;
    }
  }

  public setState(
    state: LogPanelState["data"],
    abortSignal: AbortSignal,
  ): void {
    this.workerManager.sendCommand({
      data: state,
      source: SOURCE,
      signal: abortSignal,
      type: LogCommand.SetState,
    });
    this.logCache.markCacheStale();
  }

  private loadingStateChange(realm: Realm, state: DownloadPriority | false) {
    const prevState = this.downloadState();
    this.loadingStates[realm] = state;
    const nextState = this.downloadState();

    if (prevState !== nextState) {
      this.onLoadingStateChanged({ state: nextState });
    }
  }

  private onMessage = (event: MessageEvent<MessagePayload>) => {
    if (isErrorEvent(event) && event.data.error !== undefined) {
      this.onError(event.data.error);
      return;
    }

    if (isColumnsChangedEvent(event)) {
      this.onColumnsChanged(event.data.data);
      return;
    }

    if (isInitializedEvent(event)) {
      this.onInitialized(event.data.data);
      return;
    }

    if (isLoadingStateChangeEvent(event)) {
      this.loadingStateChange("WebWorker", event.data.data.state);
      return;
    }

    if (isLogTimeIndexStateChangeEvent(event)) {
      const payload = event.data.data;
      switch (payload.state) {
        case IndexState.Building: {
          // No-op
          break;
        }
        case IndexState.Built: {
          const { timestamps: timestampsBuf, sequences: sequencesBuf } =
            payload;
          if (timestampsBuf === undefined || sequencesBuf === undefined) {
            // This would represent a programming error
            throw new Error(
              "InvalidState: Cannot set LogTimeIndexState to 'built' without also providing both timestamps and sequences.",
            );
          }
          const timestamps = new BigUint64Array(timestampsBuf);
          const sequences = new Uint32Array(sequencesBuf);
          this.logCache.setTimestampIndex(timestamps, sequences);
          this.onNumLogsChanged({ numLogs: timestamps.length });
          break;
        }
        case IndexState.Empty: {
          this.logCache.dispose();
          this.onNumLogsChanged({ numLogs: 0 });
          break;
        }
      }

      return;
    }

    if (isSearchIndexStateChangeEvent(event)) {
      const payload = event.data.data;
      this.searchIndexState = payload.state;
      this.onSearchIndexStateChanged(payload);
      return;
    }
  };
}
